import { CrudForm } from '../shared/CrudPage';
import { salaryStructureService } from '@kss/api';
import type { SalaryStructure, SalaryStructureCreate, SalaryStructureFilter } from '@kss/api';
import { loadEmployeeOptions } from '../shared/formLoaders';

/* Live-derived totals shown on the form. Gross = basic + every
   allowance. Net = gross − every deduction. Kept inline so the
   render callbacks below stay short.  Bug ref: AI Agent 2026-06-11
   — "Net Salary not calculated automatically".  */
const calcGross = (f: {
  basicSalary: number; hra: number; dearnessAllowance: number;
  medicalAllowance: number; transportAllowance: number; otherAllowances: number;
}): number =>
  Number(f.basicSalary || 0) + Number(f.hra || 0) +
  Number(f.dearnessAllowance || 0) + Number(f.medicalAllowance || 0) +
  Number(f.transportAllowance || 0) + Number(f.otherAllowances || 0);

const calcDeductions = (f: {
  providentFund: number; esi: number; professionalTax: number; taxDeduction: number;
}): number =>
  Number(f.providentFund || 0) + Number(f.esi || 0) +
  Number(f.professionalTax || 0) + Number(f.taxDeduction || 0);

const inr = (n: number): string =>
  `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;

const computedCls =
  'h-10 w-full rounded-md border border-border bg-surface-card-hover px-3 text-sm font-semibold text-content-primary flex items-center justify-end';

/**
 * Live `SalaryStructure` row carries `grossSalary` + `netSalary` —
 * the grid renders both columns. The form previously omitted them,
 * so users couldn't review the totals before saving and the columns
 * appeared "missing in form, showing in table". They are editable
 * here so a manual override is possible; the backend recomputes on
 * save if it has a server-side calculator.
 *
 * Bug ref: AI Agent (round 3) — Salary structure missing gross/net.
 */
type F = {
  employeeId: string;
  basicSalary: number; hra: number; dearnessAllowance: number;
  medicalAllowance: number; transportAllowance: number; otherAllowances: number;
  providentFund: number; esi: number; professionalTax: number; taxDeduction: number;
  grossSalary: number; netSalary: number;
  effectiveFrom: string;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  employeeId: '',
  basicSalary: 0, hra: 0, dearnessAllowance: 0,
  medicalAllowance: 0, transportAllowance: 0, otherAllowances: 0,
  providentFund: 0, esi: 0, professionalTax: 0, taxDeduction: 0,
  grossSalary: 0, netSalary: 0,
  effectiveFrom: today,
};

export default function NewSalaryStructure() {
  return (
    <CrudForm<SalaryStructure, SalaryStructureCreate, SalaryStructureFilter, F>
      title="salary structure" service={salaryStructureService} idKey="salaryStructureId"
      basePath="/panel/salary-structures" emptyForm={empty}
      fields={[
        { key: 'employeeId', label: 'Employee', type: 'search', loadOptions: loadEmployeeOptions, required: true },
        { key: 'effectiveFrom', label: 'Effective from', type: 'date', required: true },
        { key: 'basicSalary', label: 'Basic salary', type: 'number', required: true },
        { key: 'hra', label: 'HRA', type: 'number' },
        { key: 'dearnessAllowance', label: 'Dearness allowance', type: 'number' },
        { key: 'medicalAllowance', label: 'Medical allowance', type: 'number' },
        { key: 'transportAllowance', label: 'Transport allowance', type: 'number' },
        { key: 'otherAllowances', label: 'Other allowances', type: 'number' },
        { key: 'providentFund', label: 'Provident fund', type: 'number' },
        { key: 'esi', label: 'ESI', type: 'number' },
        { key: 'professionalTax', label: 'Professional tax', type: 'number' },
        { key: 'taxDeduction', label: 'Tax deduction', type: 'number' },
        /* Gross and Net are derived live from the rest of the form.
           Rendered as read-only badges so users immediately see the
           impact of any change to basic / allowance / deduction
           inputs. `toEntity` still recomputes at save time, so the
           wire payload always carries the right numbers. */
        { key: 'grossSalary', label: 'Gross salary (auto)', type: 'custom',
          render: (_v, _set, form) => (
            <div className={computedCls}>{inr(calcGross(form))}</div>
          ),
        },
        { key: 'netSalary', label: 'Net salary (auto)', type: 'custom',
          render: (_v, _set, form) => {
            const gross = calcGross(form);
            const ded = calcDeductions(form);
            return <div className={computedCls}>{inr(gross - ded)}</div>;
          },
        },
      ]}
      fromEntity={(r) => ({
        employeeId: r.employeeId ?? '',
        basicSalary: Number(r.basicSalary ?? 0),
        hra: Number(r.hra ?? 0),
        dearnessAllowance: Number(r.dearnessAllowance ?? 0),
        medicalAllowance: Number(r.medicalAllowance ?? 0),
        transportAllowance: Number(r.transportAllowance ?? 0),
        otherAllowances: Number(r.otherAllowances ?? 0),
        providentFund: Number(r.providentFund ?? 0),
        esi: Number(r.esi ?? 0),
        professionalTax: Number(r.professionalTax ?? 0),
        taxDeduction: Number(r.taxDeduction ?? 0),
        grossSalary: Number(r.grossSalary ?? 0),
        netSalary: Number(r.netSalary ?? 0),
        effectiveFrom: r.effectiveFrom?.slice(0, 10) ?? today,
      })}
      toEntity={(f, id) => ({
        salaryStructureId: id, employeeId: f.employeeId,
        basicSalary: f.basicSalary, hra: f.hra,
        dearnessAllowance: f.dearnessAllowance, medicalAllowance: f.medicalAllowance,
        transportAllowance: f.transportAllowance, otherAllowances: f.otherAllowances,
        providentFund: f.providentFund, esi: f.esi,
        professionalTax: f.professionalTax, taxDeduction: f.taxDeduction,
        /* Gross / Net are ALWAYS recomputed at save — the on-screen
           values are derived for display only, so this is the
           authoritative computation on the wire. */
        grossSalary: calcGross(f),
        netSalary: calcGross(f) - calcDeductions(f),
        effectiveFrom: f.effectiveFrom || undefined,
      })}
      validate={(f) =>
        !f.employeeId.trim() ? 'Employee is required'
          : f.basicSalary <= 0 ? 'Basic salary must be > 0'
          : null
      }
    />
  );
}
